<template>
  <div :class="['theme__popup', { show: showDialog }]" v-click-outside="forceClose">
    <div style="display: flex; flex-direction: row">
      <button
        type="button"
        style="margin-left: 0.25rem; display: flex"
        class="open-btn"
        @click="onClickOpen"
      >
        <!-- Theme source description -->
        <div v-if="themeSource">
          <!-- LDA -->
          <div v-if="themeSource.type === 'lda'">
            <SvgIconDecorative style="margin-top: -0.4rem" icon="statisticsGraph" />
            LDA-based theme
          </div>

          <!-- LSI -->
          <div v-if="themeSource.type === 'lsi'">
            <SvgIconDecorative style="margin-top: -0.4rem" icon="statisticsGraph" />
            LSI-based theme
          </div>

          <!-- BERT -->
          <div v-if="themeSource.type === 'bert'">
            <SvgIconDecorative style="margin-top: -0.4rem" icon="brain" />
            BERT sentence-based theme
          </div>

          <!-- CTM (either original or manually linked) -->
          <div v-if="themeSource.type === 'ctm' || themeSource?.link?.manually_linked_group_id">
            <SvgIconDecorative style="margin-top: -0.4rem" icon="customWrench" />
            Custom text model
          </div>

          <!-- Raw search -->
          <div v-if="themeSource.type === 'raw_search'">
            <SvgIconDecorative style="margin-top: -0.4rem" icon="search" />
            Raw search theme
          </div>
        </div>

        <!-- Manual theme -->
        <div v-if="!themeSource || themeSource.type === 'manual'">
          <SvgIconDecorative style="margin-top: -0.4rem" icon="add" />
          <VisibleText>Manual theme</VisibleText>
        </div>

        <div v-if="themeSource?.link?.manually_unlinked">
          <SvgIconDecorative style="margin-top: -0.4rem" icon="add" />
          <VisibleText>Manual theme</VisibleText>
        </div>
      </button>
      <DialogPopup
        class="theme__popup-dialog"
        header-tag="h4"
        :show="showDialog"
        @close="showDialog = false"
        style="width: 50rem"
      >
        <template #header>
          <div
            style="
              display: flex;
              flex-direction: row;
              text-align: left;
              font-size: 1rem;
              text-transform: none;
            "
          >
            Source for the theme:
          </div>
          <span
            class="theme__popup-dialog-name"
            style="
              font-family: Core-Sans;
              text-align: left;
              font-size: 1rem;
              text-transform: none;
              margin-left: 0.5rem;
              text-decoration: none;
            "
            >&nbsp;{{ themeName }}</span
          >
        </template>
        <template #body>
          <div
            style="
              text-align: left;
              display: flex;
              flex-direction: column;
              text-transform: none;
              padding-left: 0.5rem;
            "
          >
            <div class="theme-source__content">
              <div style="display: flex; flex-direction: row">
                <!-- ICON COLUMN -->
                <div>
                  <!-- LDA -->
                  <div v-if="themeSource?.type === 'lda'">
                    <SvgIconDecorative style="margin-top: -0.4rem" icon="statisticsGraph" />
                  </div>

                  <!-- LSI -->
                  <div v-if="themeSource?.type === 'lsi'">
                    <SvgIconDecorative style="margin-top: -0.4rem" icon="statisticsGraph" />
                  </div>

                  <!-- BERT -->
                  <div v-if="themeSource?.type === 'bert'">
                    <SvgIconDecorative style="margin-top: -0.4rem" icon="brain" />
                  </div>

                  <!-- CTM (original or manually linked) -->
                  <div
                    v-if="
                      themeSource?.type === 'ctm' || themeSource?.link?.manually_linked_group_id
                    "
                  >
                    <SvgIconDecorative style="margin-top: -0.4rem" icon="customWrench" />
                  </div>

                  <!-- Raw search -->
                  <div v-if="themeSource?.type === 'raw_search'">
                    <SvgIconDecorative style="margin-top: -0.4rem" icon="search" />
                  </div>

                  <!-- Manual (or missing source) -->
                  <div
                    v-if="
                      !themeSource ||
                      themeSource.type === 'manual' ||
                      themeSource?.link?.manually_unlinked
                    "
                  >
                    <SvgIconDecorative style="margin-top: -0.4rem" icon="add" />
                  </div>
                </div>

                <!-- TEXT COLUMN -->
                <div>
                  <!-- Manual -->
                  <div v-if="!themeSource || themeSource.type === 'manual'">
                    <VisibleText style="font-weight: 200">
                      This theme was created manually
                    </VisibleText>
                  </div>

                  <!-- Manually unlinked -->
                  <div v-if="themeSource?.link?.manually_unlinked">
                    <VisibleText style="font-weight: 200">
                      This theme was unlinked from a custom topic model.
                    </VisibleText>
                  </div>

                  <!-- Manually linked to CTM -->
                  <div v-if="themeSource?.link?.manually_linked_group_id">
                    <VisibleText style="font-weight: 200">
                      This theme has been linked to the Custom Text Model:
                    </VisibleText>
                    <router-link
                      :to="'/custom-text-model/' + themeSource?.link?.manually_linked_group_id"
                      target="_blank"
                    >
                      {{ themeSource?.link?.manually_linked_theme_name }}
                      <SvgIconDecorative icon="externalLink" />
                    </router-link>
                  </div>

                  <!-- LDA -->
                  <div v-if="themeSource?.type === 'lda'">
                    <VisibleText style="font-weight: 200">
                      This theme was inspired by a cluster of suggested keywords through the use of
                    </VisibleText>
                    <a
                      href="https://en.wikipedia.org/wiki/Latent_Dirichlet_allocation"
                      target="_blank"
                    >
                      LDA
                    </a>
                  </div>

                  <!-- LSI -->
                  <div v-if="themeSource?.type === 'lsi'">
                    <VisibleText style="font-weight: 200">
                      This theme was inspired by a cluster of suggested keywords through the use of
                    </VisibleText>
                    <a
                      href="https://en.wikipedia.org/wiki/Latent_semantic_analysis#Latent_semantic_indexing"
                      target="_blank"
                    >
                      LSI
                      <SvgIconDecorative icon="externalLink" />
                    </a>
                  </div>

                  <!-- BERT -->
                  <div v-if="themeSource?.type === 'bert'">
                    <VisibleText style="font-weight: 200">
                      This theme was created using our privacy-based, self-hosted Generative AI: a
                      combination of BERT and sentence tokenization.
                    </VisibleText>
                  </div>

                  <!-- CTM origin -->
                  <div v-if="themeSource?.type === 'ctm'">
                    <VisibleText style="font-weight: 200">
                      This theme was found by matching keywords to a custom dictionary:
                    </VisibleText>
                    <router-link
                      :to="'/custom-text-model/' + themeSource.origin.topic_model_group_id"
                      target="_blank"
                    >
                      {{ themeSource.origin.topic_model_group_name }}
                      <SvgIconDecorative icon="externalLink" />
                    </router-link>
                  </div>

                  <!-- Raw search -->
                  <div v-if="themeSource?.type === 'raw_search'">
                    <VisibleText style="font-weight: 200">
                      This theme was created from a raw keyword search.
                    </VisibleText>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </template>
      </DialogPopup>
    </div>
  </div>
</template>

<script>
import DialogPopup from '@/components/UI/DialogPopup.vue';
import SvgIconDecorative from '@/components/UI/Svg/SvgIconDecorative.vue';

export default {
  name: 'SourcePopup',
  components: {
    DialogPopup,
    SvgIconDecorative,
  },
  props: {
    themeName: {
      default: () => 'theme',
      type: String,
    },
    themeSource: {
      default: () => {},
      type: Object,
    },
  },
  data() {
    return {
      showDialog: false,
    };
  },
  created() {},
  methods: {
    forceClose() {
      if (this.showDialog) this.showDialog = false;
    },
    onClickOpen() {
      this.showDialog = true;
    },
  },
};
</script>

<style scoped>
.theme__popup-dialog {
  max-width: 32rem;
  width: calc(100% * 3);
}
</style>
